export const ordinalNumber = (i: number): string => {
  const j = i % 10,
    k = i % 100;
  if (j === 1 && k !== 11) {
    return i + "st";
  }
  if (j === 2 && k !== 12) {
    return i + "nd";
  }
  if (j === 3 && k !== 13) {
    return i + "rd";
  }
  return i + "th";
};

export function camelCaseToDashCase(property: string) {
  const wordList = property
    .split(/(?=[A-Z])/)
    .map((word) => word.toLocaleLowerCase());
  return wordList.join("-");
}

// check if email is valid and is a not a customer support email
export function checkIsValidProviderEmail(email: string) {
  //email regex
  const emailRegexp = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
  return emailRegexp.test(email);
}

export const pascalToTitle = (pascalString: string): string => {
  const titleString: string = pascalString.replace(/([A-Z])/g, " $1");
  const combinedTitleString =
    titleString.charAt(0).toUpperCase() + titleString.slice(1);
  return combinedTitleString.trim();
};

export const snakeToTitle = (snakeString: string) => {
  snakeString = snakeString.toLowerCase();
  snakeString = snakeString.replaceAll("_", " ");
  const splitString = snakeString.split(" ");
  for (let i = 0; i < splitString.length; i++) {
    splitString[i] =
      splitString[i].charAt(0).toUpperCase() + splitString[i].slice(1);
  }
  return splitString.join(" ");
};

export const formatPhoneNumber = (phoneNumber: string) => {
  // formats +1 xxx xxx xxxx to (xxx) xxx-xxxx
  return phoneNumber
    .replace("+1 ", "")
    .replace(/(\d{3}) (\d{3}) (\d{4})/, "($1) $2-$3");
};

/**
 * Validates an input is a number
 * @param value
 * @returns true if the value is a number
 */
export const validateAsPositiveInteger = (value: string) => {
  const asNumber = parseInt(value);
  const regex = /^\d*$/; // only allow numbers

  if (value === "" || (regex.test(value) && asNumber > 0)) {
    return true;
  }
  return false;
};

/**
 * Validates an input is a number
 * @param value
 * @returns true if the value is a number
 */
export const validateAsIntegerGreaterThanOrEqualToZero = (value: string) => {
  const asNumber = parseInt(value);
  const regex = /^\d*$/; // only allow numbers

  if (value === "" || (regex.test(value) && asNumber >= 0)) {
    return true;
  }
  return false;
};

export const validateFaxOrPhone = (
  number: string | undefined | null,
): boolean => {
  if (!number) return false;

  number = number.replace(/\s+/g, ""); // Remove all spaces
  const cleanNumber = number.replace(/\D/g, ""); // Remove non-digit characters
  const length = cleanNumber.length;

  // Correct length and area code does NOT start with 0 or 1
  if (length === 10 && !["0", "1"].includes(cleanNumber[0])) {
    return true;
  }
  // Length is 11, first digit is 1, and next digit is valid
  if (
    length === 11 &&
    cleanNumber[0] === "1" &&
    !["0", "1"].includes(cleanNumber[1])
  ) {
    return true;
  }

  return false;
};

export const validateEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Replaces instances of "and" with "&" while preserving case
 * @param text - The input string to process
 * @returns The string with "and" replaced by "&"
 */
export const replaceAndWithAmpersand = (text: string): string => {
  return text.replace(/\band\b/gi, "&");
};

/**
 * Formats a list of strings into a comma-separated list with the last item preceded by "and"
 * @param list - The list of strings to format
 * @returns The formatted string
 */
export const formatTextList = (list: string[]): string => {
  if (list.length === 1) {
    return list[0];
  }
  if (list.length === 2) {
    return `${list[0]} and ${list[1]}`;
  }
  return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`;
};

export const escapeRegex = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

export const descriptionAndCode = (
  description: string | null | undefined,
  icd10Code: string | null | undefined,
): string | null | undefined => {
  if (!description) return description;
  if (!icd10Code) return description;

  const normalizedDescription = description.toLowerCase();
  const normalizedCodePrefix = `${icd10Code.toLowerCase()}:`;

  return normalizedDescription.startsWith(normalizedCodePrefix)
    ? description
    : `${icd10Code}: ${description}`;
};

/**
 * Parses a full name into first and last name components
 */
const parseFullName = (
  fullName: string,
): { firstName: string; lastName: string | undefined } => {
  const [firstName, ...lastNameParts] = fullName.split(" ");

  return {
    firstName,
    lastName: lastNameParts.length > 0 ? lastNameParts.join(" ") : undefined,
  };
};

/**
 * Formats an account rep's full name for display (e.g., "John S" or "John" if no last name)
 */
export const formatAccountRepDisplayName = (fullName: string): string => {
  const { firstName, lastName } = parseFullName(fullName);
  return lastName ? `${firstName} ${lastName[0]}` : firstName;
};

/**
 * Formats an account rep's full name for their photo filename (e.g., "johnsmith.jpg")
 */
export const formatAccountRepPhotoFilename = (fullName: string): string => {
  const { firstName, lastName } = parseFullName(fullName);
  return `${firstName.toLowerCase()}${lastName?.toLowerCase() ?? ""}.jpg`;
};

/** "48" -> "48 hours", "168" -> "1 week", "72" -> "3 days". */
export const formatBufferHours = (hours: number): string => {
  if (hours % 168 === 0) {
    const weeks = hours / 168;
    return `${weeks} week${weeks > 1 ? "s" : ""}`;
  }
  if (hours % 24 === 0) {
    const days = hours / 24;
    return `${days} day${days > 1 ? "s" : ""}`;
  }
  return `${hours} hours`;
};
